import {
  Card,
  CardContent,
  CardHeader,
} from '@art-of-hospitality-solutions-llc/ui-kit/components/card';
import { Heading } from '@art-of-hospitality-solutions-llc/ui-kit/components/heading';
import { DateTime } from 'luxon';
import { useShiftReports } from '@Hooks/useShiftReports';
import type { AgentProfile } from '@Types/agent';
import StatusDot from './StatusDot';


function Label({ children }: { children: string }) {
  return (
    <div className="mb-1.5 text-[11px] font-bold tracking-wide text-zinc-400 uppercase">
      {children}
    </div>
  );
}

export default function AgentGlancePanel({
  soulId,
  profile,
}: {
  soulId: string;
  profile: AgentProfile;
}) {
  // Only a SHIFT_REPORT owner has venues to list; for any other agent the soul
  // gate would answer with an empty page, so don't ask.
  const { data: reports } = useShiftReports({
    soulId,
    enabled: profile.operations.includes('SHIFT_REPORT'),
  });
  const venues = Array.from(
    new Set((reports ?? []).map((r) => r.venueName)),
  );

  return (
    <Card className="lg:sticky lg:top-6">
      <CardHeader>
        <Heading level="h3">At a glance</Heading>
      </CardHeader>
      <CardContent className="space-y-5 text-sm">
        <div className="flex items-center gap-2 font-medium text-zinc-700">
          <StatusDot status={profile.status} />
          On duty
        </div>

        <div>
          <Label>{`Venues watched (${venues.length})`}</Label>
          {venues.length ? (
            <ul className="space-y-1 text-zinc-600">
              {venues.map((v) => (
                <li key={v} className="flex items-center gap-2">
                  <span className="h-1.5 w-1.5 rounded-full bg-zinc-300" />
                  {v}
                </li>
              ))}
            </ul>
          ) : (
            <p className="text-zinc-500">No venues yet.</p>
          )}
        </div>

        <div>
          <Label>Cadence</Label>
          {profile.cadence?.length ? (
            <ul className="space-y-2">
              {profile.cadence.map((run) => {
                const next = run.nextRunAt
                  ? DateTime.fromISO(run.nextRunAt)
                  : null;
                return (
                  <li key={run.jobName}>
                    <div className="font-medium text-zinc-700">{run.label}</div>
                    <div className="text-zinc-500">{run.description}</div>
                    {next?.isValid && (
                      <div className="text-xs text-zinc-400">
                        Next {next.toRelative()}
                      </div>
                    )}
                  </li>
                );
              })}
            </ul>
          ) : (
            <p className="text-zinc-500">No schedule set.</p>
          )}
        </div>
      </CardContent>
    </Card>
  );
}
